import { Alert, Form, Input, Select } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { expenseApi } from '../../../../../apis'
import {
  ActionEnum,
  AlertMessageEnum,
  getDateFormat,
} from '../../../../../common'
import {
  LoadingDataComponent,
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { CreateExpenseDto, ExpenseEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadExpenses,
  loadStations,
  setActionProcessing,
  setAlert,
} from '../../../../../stores/actions'

type ManageExpenseModalComponentProps = {
  expenseId?: number
}

const StyledLabel = styled.span`
  font-weight: bold;
`

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

export const ManageExpenseModalComponent: React.FC<ManageExpenseModalComponentProps> =
  ({ expenseId }) => {
    const dispatch = useDispatch()
    const { language } = useSelector((state: RootState) => state.language)
    const { management } = useSelector((state: RootState) => state.common)
    const { stations } = useSelector((state: RootState) => state.station)

    const [expense, setExpense] = React.useState<ExpenseEntity>()
    const [loading, setLoading] = React.useState(false)

    React.useEffect(() => {
      if (expenseId) {
        const loadData = async () => {
          try {
            setLoading(true)
            const resp = await expenseApi.getExpenseById(expenseId)
            setExpense(resp.data)
          } catch (e) {
            dispatch(
              setAlert({
                key: 'expense.loadData.fail',
                type: AlertMessageEnum.ERROR,
              })
            )
          } finally {
            setLoading(false)
          }
        }
        loadData()
      } else {
        dispatch(loadStations())
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onFinish = async (createExpenseDto: CreateExpenseDto) => {
      dispatch(setAlert())
      dispatch(setActionProcessing(true))
      switch (management.action) {
        case ActionEnum.CREATE:
          try {
            await expenseApi.createExpense(createExpenseDto)
            dispatch(loadExpenses())
            dispatch(
              setAlert({
                key: 'expense.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              await expenseApi.createExpense(createExpenseDto)
              dispatch(loadExpenses())
              dispatch(
                setAlert({
                  key: 'expense.create.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'expense.create.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
          break
        default:
          break
      }
      dispatch(setActionProcessing(false))
    }

    const body = (
      <Form onFinish={onFinish}>
        {(!management.alertMessage ||
          management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
          <>
            {loading && <LoadingDataComponent />}
            {expenseId && expense && (
              <>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.id', language)}
                    </StyledLabel>
                  }
                >
                  {expense.id}
                </Form.Item>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.createdDate', language)}
                    </StyledLabel>
                  }
                >
                  {getDateFormat(expense.createdDate)}
                </Form.Item>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.station', language)}
                    </StyledLabel>
                  }
                >
                  {`${expense.station.name} (${expense.station.address})`}
                </Form.Item>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.creator', language)}
                    </StyledLabel>
                  }
                >
                  {expense.creator.name}
                </Form.Item>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.amount', language)}
                    </StyledLabel>
                  }
                >
                  <NumberFormat
                    value={expense.amount}
                    suffixKey="common.moneyType"
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('expense.reason', language)}
                    </StyledLabel>
                  }
                >
                  {expense.reason}
                </Form.Item>
                {expense.fuelImport && (
                  <>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('expense.fuelImport', language)}
                        </StyledLabel>
                      }
                    >
                      {expense.fuelImport.name}
                    </Form.Item>
                    <Form.Item
                      label={
                        <StyledLabel>
                          {translate('fuelImport.importDate', language)}
                        </StyledLabel>
                      }
                    >
                      {getDateFormat(expense.fuelImport.importDate)}
                    </Form.Item>
                  </>
                )}
              </>
            )}
            {!expenseId && (
              <>
                <Alert
                  showIcon
                  closable
                  type="warning"
                  message={translate('common.alert.warning', language)}
                  description={translate(
                    'expense.warning.checkData.description',
                    language
                  )}
                  style={{ marginBottom: '10px' }}
                />
                <StyledFormItem
                  name="reason"
                  label={
                    <StyledLabel>
                      {translate('expense.reason', language)}
                    </StyledLabel>
                  }
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || !value.trim().length) {
                          return Promise.reject(
                            new Error(
                              translate('expense.invalid.reason', language)
                            )
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={translate('expense.reason', language)}
                    allowClear
                  />
                </StyledFormItem>
                <StyledFormItem
                  name="amount"
                  label={
                    <StyledLabel>
                      {translate('expense.amount', language)}
                    </StyledLabel>
                  }
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || value <= 0) {
                          return Promise.reject(
                            new Error(
                              translate('expense.invalid.amount', language)
                            )
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    type="number"
                    placeholder={translate('expense.amount', language)}
                    allowClear
                  />
                </StyledFormItem>
                <StyledFormItem
                  name="stationId"
                  label={
                    <StyledLabel>
                      {translate('expense.station', language)}
                    </StyledLabel>
                  }
                  rules={[
                    {
                      required: true,
                      message: translate('expense.required.station', language),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    onSearch={() => {}}
                    style={{ width: '100%' }}
                    filterOption={(input, option: any) =>
                      (option.children || '')
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    optionFilterProp="children"
                    placeholder={translate('expense.station', language)}
                  >
                    {stations.map((item) => (
                      <Select.Option value={item.id} key={item.id}>
                        {`${item.name} (${item.address})`}
                      </Select.Option>
                    ))}
                  </Select>
                </StyledFormItem>
              </>
            )}
          </>
        )}
        {!loading && (
          <ManageModalFooterComponent hasDefaultButtons={!expenseId} />
        )}
      </Form>
    )

    return <ManageModalComponent body={body} />
  }
